import type {
  CopilotChip,
  CopilotConciergeItem,
  CopilotConciergeSection,
} from "@CommonApi/merchflows/_merchflowId_/config-copilot";
import { Color, Flex, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { styledSafe } from "src/utils/styledComponent";
import type { SketchPalette } from "../store/sketchPalette";
import { OSketch } from "./OSketch";

const Section = styledSafe("section")`
  border-top: 1px solid ${Color.lightGrayHover};
  padding: 18px 0;
`;

const Choice = styledSafe("button")`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 7px;
  width: 100%;
  padding: 14px;
  text-align: left;
  color: ${Color.textMain};
  border: 1px solid ${Color.lightGrayHover};
  border-radius: 6px;
  background: ${Color.white};
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: ${Color.primary};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const ChoiceGrid = styledSafe("div")`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 580px) {
    grid-template-columns: 1fr;
  }
`;

interface Props {
  sections: CopilotConciergeSection[];
  ready: boolean;
  palette: SketchPalette;
  disabled: boolean;
  onPick: (chip: CopilotChip) => void;
  onRetry: () => void;
}

const firstDecisionOf = (sections: CopilotConciergeSection[]) => {
  const pending = sections
    .flatMap((section) => section.items)
    .filter((item) => item.status === "needs_decision");
  return (
    pending.find((item) => item.choices?.length) ??
    pending.find((item) => item.kind === "limitation") ??
    pending[0]
  );
};

export const OConcierge = ({ sections, ready, palette, disabled, onPick, onRetry }: Props) => {
  const { t } = useTranslation("common");
  const active = firstDecisionOf(sections);
  const plan = sections.find((section) => section.id === "plan");
  const isPlanActive = plan?.items.some((item) => item === active) ?? false;
  const baseEvidence = plan?.items.find((item) => item.observation?.sketch)?.observation;
  const planFact = plan?.items.find((item) => item.kind === "fact");
  const proposals = plan?.items.filter((item) => item.kind === "proposal") ?? [];
  const planDetails =
    plan?.items.filter(
      (item) => item !== planFact && item !== active && item.kind !== "proposal",
    ) ?? [];
  planDetails.push(...proposals.slice(1));

  const titleOf = (section: CopilotConciergeSection) =>
    section.id === "plan"
      ? t("configCopilot.conciergePlanTitle")
      : section.id === "ambiguities"
        ? t("configCopilot.conciergeAmbiguitiesTitle")
        : t("configCopilot.conciergeExceptionsTitle");

  const statusOf = (item: CopilotConciergeItem) =>
    item.status === "proposed"
      ? t("configCopilot.conciergeProposed")
      : item.status === "accepted"
        ? t("configCopilot.conciergeAccepted")
        : item.kind === "fact" &&
            (item.provenance === "base_measurement" || item.provenance === "store_measurement")
          ? t("configCopilot.conciergeMeasured")
          : item.status === "retained"
            ? t("configCopilot.conciergeCurrent")
            : item.status === "known_limit"
              ? t("configCopilot.conciergeLimit")
              : t("configCopilot.conciergeNeedsDecision");

  const evidenceOf = (item: CopilotConciergeItem) =>
    item.observation && (
      <Flex column gap="8px" data-testid="o-concierge-evidence">
        <Text variant="body2">{item.observation.line}</Text>
        {item.observation.sketch && <OSketch pair={item.observation.sketch} palette={palette} />}
      </Flex>
    );

  const choicesOf = (item: CopilotConciergeItem) =>
    item.choices &&
    item.choices.length > 0 && (
      <ChoiceGrid data-testid="o-concierge-choices">
        {item.choices.map((choice) => (
          <Choice
            key={`${choice.label}-${choice.pick?.kind ?? "say"}`}
            type="button"
            aria-label={choice.label}
            disabled={disabled}
            onClick={() => onPick(choice)}
          >
            {choice.tradeoff?.recommended && (
              <Text variant="small1" bold color={Color.primary}>
                {t("configCopilot.conciergeRecommendation")}
              </Text>
            )}
            <Text variant="body2" bold>
              {choice.label}
            </Text>
            {choice.tradeoff?.gain && <Text variant="body2">{choice.tradeoff.gain}</Text>}
            {choice.tradeoff?.cost && (
              <Text variant="small1" color={Color.textSecondary}>
                {t("configCopilot.choiceCost", { cost: choice.tradeoff.cost })}
              </Text>
            )}
          </Choice>
        ))}
      </ChoiceGrid>
    );

  const retryOf = (item: CopilotConciergeItem) =>
    item.kind === "limitation" &&
    !item.choices?.length && (
      <Choice type="button" disabled={disabled} onClick={onRetry}>
        <Text variant="body2" bold>
          {t("configCopilot.conciergeRetryReview")}
        </Text>
      </Choice>
    );

  return (
    <Flex column gap="8px" data-testid="o-concierge">
      {plan && (
        <Section data-testid="o-concierge-plan">
          {(active && !isPlanActive) || ready ? (
            <details data-testid="o-concierge-plan-fold">
              <summary>
                {titleOf(plan)} · {t("configCopilot.conciergePlanReviewed")}
              </summary>
              <Flex column gap="12px" padding="12px 0 0 0">
                {plan.items.map((item) => (
                  <Flex key={item.id} column gap="3px">
                    <Text variant="body2" bold>
                      {item.title} · {statusOf(item)}
                    </Text>
                    <Text variant="body2">{item.detail}</Text>
                  </Flex>
                ))}
              </Flex>
            </details>
          ) : (
            <Flex column gap="14px">
              <Text variant="body1" bold>
                {titleOf(plan)}
              </Text>
              {planFact && <Text variant="body2">{planFact.detail}</Text>}
              {baseEvidence && (
                <Flex column gap="8px" data-testid="o-concierge-evidence">
                  <Text variant="body2">{baseEvidence.line}</Text>
                  {baseEvidence.sketch && <OSketch pair={baseEvidence.sketch} palette={palette} />}
                </Flex>
              )}
              {proposals.slice(0, 1).map((item) => (
                <Flex key={item.id} column gap="6px" data-testid="o-concierge-plan-item">
                  <Flex gap="8px" align="center" flexWrap="wrap">
                    <Text variant="body2" bold>
                      {item.title}
                    </Text>
                    <Text variant="small1" color={Color.textSecondary}>
                      {statusOf(item)}
                    </Text>
                  </Flex>
                  <Text variant="body2">{item.detail}</Text>
                </Flex>
              ))}
              {proposals.length > 1 && (
                <Flex column gap="5px" data-testid="o-concierge-other-proposals">
                  <Text variant="small1" bold>
                    {t("configCopilot.conciergeAlsoProposed")}
                  </Text>
                  <Text variant="body2">
                    {proposals
                      .slice(1)
                      .map((item) => item.title)
                      .join(" · ")}
                  </Text>
                </Flex>
              )}
              {planDetails.length > 0 && (
                <details data-testid="o-concierge-plan-details">
                  <summary>{t("configCopilot.conciergeSupportingDetails")}</summary>
                  <Flex column gap="8px" padding="8px 0 0 0">
                    {planDetails.map((item) => (
                      <Flex key={item.id} column gap="3px">
                        <Text variant="body2" bold>
                          {item.title}
                        </Text>
                        <Text variant="body2">{item.detail}</Text>
                      </Flex>
                    ))}
                  </Flex>
                </details>
              )}
              {active && isPlanActive && (
                <Flex column gap="8px" data-testid="o-concierge-decision">
                  <Text variant="body2" bold>
                    {active.title}
                  </Text>
                  <Text variant="body2">{active.detail}</Text>
                  {!baseEvidence && evidenceOf(active)}
                  {choicesOf(active)}
                  {retryOf(active)}
                </Flex>
              )}
            </Flex>
          )}
        </Section>
      )}

      {sections
        .filter((section) => section.id !== "plan")
        .map((section) => {
          const current = section.items.find((item) => item === active);
          const context = section.items.filter(
            (item) => item !== current && (item.kind === "fact" || item.kind === "limitation"),
          );
          return (
            <Section key={section.id} data-testid={`o-concierge-${section.id}`}>
              <Flex column gap="12px">
                <Text variant="body1" bold>
                  {titleOf(section)}
                </Text>
                {current ? (
                  <Flex column gap="10px" data-testid="o-concierge-decision">
                    {context.slice(0, 2).map((item) => (
                      <Flex key={item.id} column gap="3px" data-testid="o-concierge-context">
                        <Text variant="body2" bold>
                          {item.title}
                        </Text>
                        <Text variant="body2">{item.detail}</Text>
                        {evidenceOf(item)}
                      </Flex>
                    ))}
                    {context.length > 2 && (
                      <details>
                        <summary>
                          {t("configCopilot.conciergeMoreEvidence", { count: context.length - 2 })}
                        </summary>
                        <Flex column gap="8px" padding="8px 0 0 0">
                          {context.slice(2).map((item) => (
                            <Flex key={item.id} column gap="3px">
                              <Text variant="body2" bold>
                                {item.title}
                              </Text>
                              <Text variant="body2">{item.detail}</Text>
                            </Flex>
                          ))}
                        </Flex>
                      </details>
                    )}
                    <Text variant="body2" bold>
                      {current.title}
                    </Text>
                    <Text variant="body2">{current.detail}</Text>
                    {evidenceOf(current)}
                    {choicesOf(current)}
                    {retryOf(current)}
                  </Flex>
                ) : active ? (
                  <Text variant="small1" color={Color.textSecondary}>
                    {t("configCopilot.conciergeNext")}
                  </Text>
                ) : ready && section.items.length > 0 ? (
                  <details>
                    <summary>{t("configCopilot.conciergeSectionDetails")}</summary>
                    <Flex column gap="8px" padding="8px 0 0 0">
                      {section.items.map((item) => (
                        <Flex key={item.id} column gap="3px">
                          <Text variant="body2" bold>
                            {item.title} · {statusOf(item)}
                          </Text>
                          <Text variant="body2">{item.detail}</Text>
                        </Flex>
                      ))}
                    </Flex>
                  </details>
                ) : (
                  <Flex column gap="8px">
                    {section.items.filter((item) => item.status !== "needs_decision").length ===
                    0 ? (
                      <Text variant="small1" color={Color.textSecondary}>
                        {t(
                          active
                            ? "configCopilot.conciergeNext"
                            : "configCopilot.conciergeNoExceptions",
                        )}
                      </Text>
                    ) : (
                      section.items
                        .filter((item) => item.status !== "needs_decision")
                        .map((item) => (
                          <Flex key={item.id} column gap="3px">
                            <Text variant="body2" bold>
                              {item.title} · {statusOf(item)}
                            </Text>
                            <Text variant="body2">{item.detail}</Text>
                          </Flex>
                        ))
                    )}
                  </Flex>
                )}
              </Flex>
            </Section>
          );
        })}

      {ready && (
        <div data-testid="o-concierge-ready">
          <Text variant="body2">{t("configCopilot.conciergeReady")}</Text>
        </div>
      )}
      {!ready && !active && (
        <div data-testid="o-concierge-pending">
          <Text variant="body2" color={Color.textSecondary}>
            {t("configCopilot.conciergePending")}
          </Text>
        </div>
      )}
    </Flex>
  );
};
